import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import dlrLogo from '../assets/dlr-logo.svg';

interface MenuItem {
  label: string;
  path: string;
  icon: string;
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount, markRead } = useNotifications();

  const menuItems: MenuItem[] = [
    { label: 'Dashboard', path: '/dashboard', icon: 'space_dashboard' },
    { label: 'Meine Flüge', path: '/flights', icon: 'flight_takeoff' },
    { label: 'Meine Freunde', path: '/friends', icon: 'group' },
    { label: 'Alle Flüge', path: '/all-flights', icon: 'travel_explore' },
    { label: 'News', path: '/news', icon: 'newspaper' },
    { label: 'Flugbuch /\nFahrtenbuch', path: '/logbook', icon: 'menu_book' },
    { label: 'Einstellungen', path: '/settings', icon: 'settings' },
    { label: 'Über das Projekt', path: '/about', icon: 'info' },
  ];

  const mainMenuItems = menuItems.slice(0, -1);
  const bottomMenuItem = menuItems[menuItems.length - 1];

  const isActive = (path: string) => {
    // Exact match
    if (location.pathname === path) return true;

    // Special case: /flights/:id should highlight "Meine Flüge"
    if (path === '/flights' && location.pathname.startsWith('/flights/')) return true;

    // Special case: /news/:id should highlight "News"
    if (path === '/news' && location.pathname.startsWith('/news/')) return true;

    // Special case: /all-flights/:id should highlight "Alle Flüge"
    if (path === '/all-flights' && location.pathname.startsWith('/all-flights/')) return true;

    // Special case: /pilots/:userid is accessed from friends or all-flights, highlight "Meine Freunde"
    if (path === '/friends' && location.pathname.startsWith('/pilots/')) return true;

    return false;
  };

  return (
    <nav
      style={{
        width: '260px',
        flexShrink: 0,
        padding: '24px',
        backgroundColor: '#f8f9fa',
        minHeight: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {mainMenuItems.map((item) => {
        const isMyFlights = item.path === '/flights';
        const showBadge = isMyFlights && unreadCount > 0;
        return (
          <div
            key={item.path}
            onClick={() => {
              if (isMyFlights && unreadCount > 0) markRead();
              navigate(item.path);
            }}
            style={{
              background: isActive(item.path) ? '#32BDEF' : 'white',
              color: isActive(item.path) ? 'white' : '#5a6c7d',
              padding: '16px 20px',
              minHeight: '64px',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              fontWeight: 500,
              fontSize: '16px',
            }}
            onMouseEnter={(e) => {
              if (!isActive(item.path)) e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span
                className={`material-symbols-outlined ${isActive(item.path) ? 'filled' : ''}`}
                style={{
                  fontSize: '22px',
                  lineHeight: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  userSelect: 'none',
                  fontVariationSettings: isActive(item.path) ? "'FILL' 1" : "'FILL' 0",
                }}
              >
                {item.icon}
              </span>
              <span style={{ whiteSpace: 'pre-line', lineHeight: '1.25' }}>{item.label}</span>
              {showBadge && (
                <span style={{
                  background: '#e53e3e',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '1px 7px',
                  fontSize: '11px',
                  fontWeight: 700,
                  lineHeight: '16px',
                  minWidth: '18px',
                  textAlign: 'center',
                  flexShrink: 0,
                }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </div>
        );
      })}
      <div style={{ flex: 1 }} />
      <div
        onClick={() => navigate(bottomMenuItem.path)}
        style={{
          background: isActive(bottomMenuItem.path)
            ? '#32BDEF'
            : 'white',
          color: isActive(bottomMenuItem.path) ? 'white' : '#5a6c7d',
          padding: '16px 20px',
          minHeight: '64px',
          borderRadius: '12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
          fontWeight: 500,
          fontSize: '16px',
        }}
        onMouseEnter={(e) => {
          if (!isActive(bottomMenuItem.path)) {
            e.currentTarget.style.transform = 'translateX(4px)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateX(0)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            className={`material-symbols-outlined ${isActive(bottomMenuItem.path) ? 'filled' : ''}`}
            style={{
              fontSize: '22px',
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              userSelect: 'none',
              fontVariationSettings: isActive(bottomMenuItem.path) ? "'FILL' 1" : "'FILL' 0",
            }}
          >
            {bottomMenuItem.icon}
          </span>
          <span style={{ whiteSpace: 'pre-line', lineHeight: '1.25' }}>{bottomMenuItem.label}</span>
        </div>
      </div>
      <div style={{ marginTop: '20px' }}>
        <img
          src={dlrLogo}
          alt="DLR Logo"
          style={{ width: '60px', height: 'auto' }}
        />
      </div>
    </nav>
  );
};

export default Sidebar;
